import { apiRequest } from '../../../utils/api';
import { normalizeCriteriaValue } from './steps/criteriaOptions';
import {
  CouldBuySeed,
  CouldBuyFormState,
  NamedArea,
  MAX_PROPERTY_DESCRIPTION_LENGTH,
} from './types';

// Shape of one area interest as returned by GET /area-interests (JAA-277).
// display_areas is what the buyer originally chose; criteria and notify flags
// round-trip so the edit form can be pre-filled exactly.
export interface AreaInterest {
  area_interest_id: string;
  // Null when the buyer cleared the anchor building (district-only interest).
  anchor_building_id: string | null;
  display_areas: Array<
    | { kind: 'circle'; radius_m: number; lng?: number; lat?: number }
    | {
        kind: 'area';
        ext_id: string;
        name: string;
        level: 'sub-area' | 'district' | 'city';
      }
    // Display-only: the anchor building shown on the success card for a
    // building-only interest (never persisted — those go via off-market).
    | { kind: 'building'; name: string }
  >;
  criteria: {
    size_min?: number;
    size_max?: number;
    rooms_min?: string;
    rooms_flexible?: boolean;
    price_min?: number;
    price_max?: number;
    include_unpriced?: boolean;
    floor?: string;
    renovation_status?: string;
    amenities?: string[];
    property_description?: string;
  };
  notify_email: boolean;
  notify_sms: boolean;
  created_at: string;
  /** Anchor building's address, labelling the circle ("<street> ± 500 m"). */
  anchor_address?: { street?: string; houseNumber?: string; city?: string } | null;
  /** Owner's Stripe-Identity status; drives the card's verified shield. */
  verified?: boolean;
}

// One anonymized area interest as a demand card (JAA-282). The backend merges
// these into the off-market listings (GET /off-market-listings-in-bounds) tagged
// `type: 'area_demand'`, already distance-sorted; this is the shape of those rows.
// The AreaInterest fields plus the anchor building centroid (for map recentre) and
// its first address (for the "<street> ± 500 m" label). No buyer identity.
export interface AreaDemandCard extends AreaInterest {
  match_status?: 'confirmed' | 'unconfirmed';
  unconfirmed_fields?: string[];
  /** Server-computed: true only on the signed-in viewer's own interest. */
  isUserOwnEvent?: boolean;
  anchor_lng: number | null;
  anchor_lat: number | null;
  anchor_address: {
    street?: string;
    houseNumber?: string;
    city?: string;
  } | null;
}

// Fetch the anonymized area interests whose footprint covers a building
// (GET /area-interests/for-building) — the area-buyer rows the building preview
// card, the building view and the single-listing view show alongside
// building-level buyers. Public endpoint; rows are the same `area_demand` shape
// the in-bounds listings merge returns (no buyer identity).
//
// `coords` is a FALLBACK, not an override: the endpoint answers from the
// building's own centroid whenever it has one, so every surface asking about one
// building gets one answer no matter which point it happens to hold. It is worth
// passing anyway — the map draws buildings this database has never stored, and
// for those the client's point is the only one that exists (JAA-347).
//
// Returns null when the request failed or was aborted, which an empty list
// cannot express: a building legitimately covered by no area is the common case,
// and a caller that blanks its row on a dropped request loses rows it already
// had.
export const getAreaInterestsForBuilding = async (
  buildingId: string,
  options: {
    coords?: { lat: number; lng: number } | null;
    signal?: AbortSignal;
  } = {},
): Promise<AreaDemandCard[] | null> => {
  try {
    const params = new URLSearchParams({ 'building-id': buildingId });
    if (options.coords) {
      params.set('lat', String(options.coords.lat));
      params.set('lng', String(options.coords.lng));
    }
    const data = await apiRequest(`/area-interests/for-building?${params}`, {
      signal: options.signal,
    });
    return Array.isArray(data?.area_interests) ? data.area_interests : [];
  } catch {
    return null;
  }
};

// Fetch the authenticated user's area interests (JAA-277 GET /area-interests).
// Failures propagate: every caller already has its own catch, and swallowing
// them here made MyAreasView's error state (and its retry) unreachable.
export const getMyAreaInterests = async (): Promise<AreaInterest[]> => {
  const data = await apiRequest('/area-interests');
  const rows: AreaInterest[] = Array.isArray(data)
    ? data
    : (data?.area_interests ?? []);
  return rows;
};

// Optimistically remove an area interest by id. Callers should remove the item
// from local state immediately (optimistic), then call this; on failure the
// caller may re-fetch to restore the list.
export const deleteAreaInterest = async (id: string): Promise<void> => {
  await apiRequest(`/area-interests/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
};

// The subset of the off-market POST response the could-buy flow acts on:
// whether the buy intent is now active, and how many sellers on the building
// already match the buyer's criteria. Creating the interest opens no
// conversation; the buyer starts chats per listing from the Activity row.
export interface BuildingCouldBuyResult {
  could_buy_selected: boolean;
  willingness_id?: string;
  // Criteria-matched sellers already listing on this building (the same
  // predicate the Activity row's listing_count uses). The success card shows
  // the count and links to the row detail.
  matched_listing_count?: number;
}

// Create a SINGLE-BUILDING "could buy" interest on the anchor building (the
// building-only branch of the could-buy flow, when the buyer added no area).
// This is the existing off-market "could buy" path — POST /off-market-willingness
// — alert-only like an area interest: matching sellers surface in the Activity
// view and the daily digest, never as auto-created conversations.
// We reconstruct the minimal `selected_building` the backend reads (id +
// addresses[0] + geometry) from the flow seed; the heavier off-market modal
// passes the full building object, but the backend only needs these fields.
export const postBuildingCouldBuy = async (
  seed: CouldBuySeed,
  form?: CouldBuyFormState,
): Promise<BuildingCouldBuyResult> => {
  const selectedBuilding = {
    id: seed.buildingId,
    addresses: [
      {
        street: seed.address.street,
        houseNumber: seed.address.houseNumber,
        city: seed.address.city,
        country: seed.address.country,
        coordinates: seed.coordinates ?? undefined,
      },
    ],
    ...(seed.coordinates
      ? {
          geometry: {
            type: 'Point' as const,
            coordinates: [seed.coordinates.lng, seed.coordinates.lat],
          },
        }
      : {}),
  };

  return apiRequest('/off-market-willingness', {
    method: 'POST',
    body: {
      could_buy: true,
      selected_building: selectedBuilding,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      source: 'could_buy_flow',
      // Everything the buyer typed, sent at create time. Without this the
      // interest was stored empty and only gained its criteria if the buyer
      // later happened to open the edit modal.
      ...(form ? { criteria: criteriaFromForm(form) } : {}),
      // Digest notify flags, stored per interest like an area interest's.
      ...(form
        ? { notify_email: form.notifyEmail, notify_sms: form.notifySms }
        : {}),
    },
  });
};

// Update an existing SINGLE-BUILDING "could buy" interest's editable fields
// (buy-listing edit, JAA-304) — PUT /off-market-willingness/:id, the same
// endpoint the legacy edit modal uses. The could-buy form only carries
// description/size/rooms (it has no floor/elevator/balcony/price fields), so
// the body sends only those known property_* keys. This is safe, not just
// convenient: update_off_market_willingness (jaakkocom routes/
// add_off_market_willingness.py) keys its $set purely on which property_*
// keys are present in the request body, so omitted keys are left untouched in
// Mongo rather than nulled — unlike a full-replace PUT, a partial body here
// cannot clobber floor/elevator/balcony/renovation/price/images left over from
// this building/user's own prior sell listing.
export const putBuildingCouldBuy = async (
  willingnessId: string,
  form: CouldBuyFormState,
): Promise<BuildingCouldBuyResult> => {
  return apiRequest(`/off-market-willingness/${willingnessId}`, {
    method: 'PUT',
    // One `criteria` object, the same shape an area interest stores. The backend
    // writes the flat property_* mirrors itself, so nothing here has to know
    // about them — and `floor` stays out of property_floor, which means a storey
    // number on a sell listing, not the buyer's 'highest'/'mid'/'lowest'.
    // The notify flags ride along so an edit saves the toggles too; the form
    // prefilled them from the stored doc (listingToCouldBuyForm), so resending
    // is never a clobber.
    body: {
      criteria: criteriaFromForm(form),
      notify_email: form.notifyEmail,
      notify_sms: form.notifySms,
    },
  });
};

// Convert the display_areas of an area interest into the NamedArea picks the
// form state expects (area-kind rows only; the circle is handled separately).
export const namedAreasFromDisplayAreas = (
  displayAreas: AreaInterest['display_areas'],
): NamedArea[] =>
  displayAreas
    .filter((a): a is Extract<typeof a, { kind: 'area' }> => a.kind === 'area')
    .map((a) => ({
      kind: 'area',
      ext_id: a.ext_id,
      name: a.name,
      level: a.level,
    }));

// Build the display_areas[] payload from form state: the fixed 500 m circle (if
// selected) followed by the picked named areas. Single source of truth shared by
// the POST/PUT submit and the success-screen card preview. The circle is defined
// as 500 m around the anchor building, so it only serializes while the anchor is
// selected — a bare "500 m radius" with no anchor is meaningless to display.
export const displayAreasFromForm = (
  form: CouldBuyFormState,
): AreaInterest['display_areas'] => {
  const areas: AreaInterest['display_areas'] = form.areas.map((a) => ({
    kind: 'area',
    ext_id: a.ext_id,
    name: a.name,
    level: a.level,
  }));
  if (form.circleSelected && form.anchorSelected) {
    // The centre travels with the circle so the interest is self-contained: the
    // backend used to look it up from the anchor building, and a building it had
    // never stored produced an interest with no footprint at all (JAA-328).
    const c = form.circleCentre;
    areas.unshift(
      c
        ? { kind: 'circle', radius_m: 500, lng: c.lng, lat: c.lat }
        : { kind: 'circle', radius_m: 500 },
    );
  }
  return areas;
};

// Build the criteria payload from form state, omitting empty fields. Shared by the
// submit and the success-card preview so both read the same criteria summary.
export const criteriaFromForm = (
  form: CouldBuyFormState,
): AreaInterest['criteria'] => {
  const criteria: AreaInterest['criteria'] = {};
  if (form.sizeMin !== '') criteria.size_min = Number(form.sizeMin);
  if (form.sizeMax !== '') criteria.size_max = Number(form.sizeMax);
  if (form.roomsMin !== '') criteria.rooms_min = form.roomsMin;
  if (form.roomsFlexible) criteria.rooms_flexible = true;
  if (form.priceMin !== '') criteria.price_min = Number(form.priceMin);
  if (form.priceMax !== '') criteria.price_max = Number(form.priceMax);
  if (form.floor !== '') criteria.floor = form.floor;
  if (form.renovation !== '') criteria.renovation_status = form.renovation;
  if (form.amenities.length > 0) criteria.amenities = form.amenities;
  const description = form.description
    .trim()
    .slice(0, MAX_PROPERTY_DESCRIPTION_LENGTH);
  if (description !== '') criteria.property_description = description;
  return criteria;
};

// Assemble a full AreaInterest from form state for previewing the not-yet-saved
// interest as a card (the success screen). Id/anchor/created_at are placeholders —
// the card only reads display_areas, criteria, and the notify flags. For a
// building-only interest (no circle/areas), `anchorLabel` is shown as the location
// so the card isn't empty.
/**
 * Rebuild the CouldBuyFlow form state from a stored interest, so editing
 * re-opens the flow exactly as the user left it. The inverse of
 * areaInterestFromForm; used wherever an interest can be edited (My areas, the
 * Activity row detail).
 */
export const formFromAreaInterest = (
  interest: AreaInterest,
): CouldBuyFormState => ({
  areas: namedAreasFromDisplayAreas(interest.display_areas),
  circleSelected: interest.display_areas.some((a) => a.kind === 'circle'),
  // Carry the stored centre back into the form. The edit path opens with a
  // synthetic seed that has no coordinates, so without this a re-save would
  // drop the circle's centre (JAA-328).
  circleCentre: (() => {
    const c = interest.display_areas.find(
      (a): a is { kind: 'circle'; radius_m: number; lng?: number; lat?: number } =>
        a.kind === 'circle',
    );
    return typeof c?.lng === 'number' && typeof c?.lat === 'number'
      ? { lat: c.lat, lng: c.lng }
      : null;
  })(),
  // The anchor building is part of the interest only when it was kept on
  // create (stored id). A district-only interest re-opens with no building.
  anchorSelected: interest.anchor_building_id != null,
  sizeMin:
    interest.criteria.size_min != null
      ? String(interest.criteria.size_min)
      : '',
  sizeMax:
    interest.criteria.size_max != null
      ? String(interest.criteria.size_max)
      : '',
  roomsMin: interest.criteria.rooms_min ?? '',
  roomsFlexible: interest.criteria.rooms_flexible ?? false,
  priceMin:
    interest.criteria.price_min != null
      ? String(interest.criteria.price_min)
      : '',
  priceMax:
    interest.criteria.price_max != null
      ? String(interest.criteria.price_max)
      : '',
  floor: interest.criteria.floor ?? '',
  renovation: normalizeCriteriaValue(interest.criteria.renovation_status) ?? '',
  amenities: interest.criteria.amenities ?? [],
  description: interest.criteria.property_description ?? '',
  notifyEmail: interest.notify_email,
  notifySms: interest.notify_sms,
});

export const areaInterestFromForm = (
  form: CouldBuyFormState,
  anchorLabel?: string,
): AreaInterest => {
  const display_areas = displayAreasFromForm(form);
  if (display_areas.length === 0 && form.anchorSelected && anchorLabel) {
    display_areas.push({ kind: 'building', name: anchorLabel });
  }
  return {
    area_interest_id: '',
    anchor_building_id: '',
    display_areas,
    criteria: criteriaFromForm(form),
    notify_email: form.notifyEmail,
    notify_sms: form.notifySms,
    created_at: '',
  };
};
